import React, { useState, useMemo } from 'react';
import { Row, Col, Typography, Alert, Space, Tag, Button, message } from 'antd';
import { EnvironmentOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import MapCore from '../mapping/MapCore';
import SiteSelectionPanel from '../mapping/SiteSelectionPanel';
import AdvancedDrawingTools from '../mapping/AdvancedDrawingTools';
import HazardOverlayLayer from '../mapping/HazardOverlayLayer';
import MapLegend from '../mapping/MapLegend';
import SiteListPopup from '../mapping/SiteListPopup';

const { Title, Text } = Typography;

/**
 * CrisisMapTab - Map view within incident details
 * Shows crisis location, organizational sites, and enables mass communication
 * 🆕 PHASE 4: NOW WITH MANUAL CRISIS LOCATION REPOSITIONING
 */
const CrisisMapTab = ({ 
  incidentId, 
  incident
}) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  
  // Local state for map functionality
  const [selectedSites, setSelectedSites] = useState([]);
  const [measurementMode, setMeasurementMode] = useState('idle');
  const [showSiteIcons, setShowSiteIcons] = useState(true);
  const [siteColorMode, setSiteColorMode] = useState('status');
  const [allSites, setAllSites] = useState([]);
  const [siteCounts, setSiteCounts] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [unitPreference, setUnitPreference] = useState('km');
  const [ringStyleConfig, setRingStyleConfig] = useState({
    colorScheme: 'red',
    customColors: { primary: '#ff0000', rings: '#ff0000' },
    opacity: { primary: 0.8, rings: 0.4, fill: 0.1 },
    strokeWeight: { primary: 2, rings: 1 }
  });

  // 🎯 CRISIS FIX: Popup state for circle detection
  const [showSitePopup, setShowSitePopup] = useState(false);
  const [sitePopupData, setSitePopupData] = useState(null);
  
  // Simple local handlers
  const handleSitesSelected = (sites) => setSelectedSites(sites);
  const handleMapRefresh = () => setSelectedSites([]);
  const handleMapLoad = (mapInstanceRef) => {
    setMapInstance(mapInstanceRef);

  };
  const handleMeasurementModeChange = (mode) => setMeasurementMode(mode);
  const handleSitesLoaded = (sites, counts) => {
    setAllSites(sites);
    setSiteCounts(counts);
  };
  const handleUnitPreferenceChange = (newUnit) => setUnitPreference(newUnit);
  const handleRingStyleChange = (newConfig) => setRingStyleConfig(newConfig);

  // 🎯 CRISIS FIX: Popup state callbacks for circle detection  
  const handleSitePopupStateChange = (isVisible) => {
    console.log('🎯 CrisisMapTab: Popup state changed to:', isVisible);
    setShowSitePopup(isVisible);
  };

  const handleSitePopupDataChange = (popupData) => {
    console.log('🎯 CrisisMapTab: Popup data changed:', popupData);
    setSitePopupData(popupData);
  };

  // 🎯 CRITICAL FIX: Circle detection callback - calls MapCore handler like Dashboard does
  const handleCircleComplete = (circle) => {
    console.log('🎯 CrisisMapTab: handleCircleComplete called with circle:', circle);
    
    const handler = mapInstance?.measurementFunctionsRef?.current?.handleCircleComplete;
    console.log('🎯 CrisisMapTab: handleCircleComplete handler found:', !!handler, typeof handler);
    
    if (!handler) {
      console.log('🚨 CrisisMapTab: handleCircleComplete not ready, mapInstance:', !!mapInstance);
      return;
    }
    
    console.log('🎯 CrisisMapTab: Calling handleCircleComplete with circle:', circle);
    return handler(circle);
  };

  // 🆕 PHASE 4: Handle manual crisis location repositioning
  const handleCrisisLocationDrag = async (newPosition) => {
    if (!incidentId || !newPosition) {
      console.error('🎯 PHASE 4: Missing incidentId or newPosition for crisis location update');
      return;
    }

    try {
      console.log('🎯 PHASE 4: Updating incident with new crisis location:', {
        incidentId,
        newPosition
      });

      // 🔄 REVERSE GEOCODING: Get human-readable address from coordinates
      let newLocationAddress = null;
      let reverseGeocodeName = null;
      
      try {
        console.log('🔄 Performing reverse geocoding for dragged location...');
        const geocodingService = (await import('../../services/geocodingService')).default;
        
        // Get the formatted address
        reverseGeocodeName = await geocodingService.reverseGeocode(
          newPosition.latitude, 
          newPosition.longitude
        );
        
        if (reverseGeocodeName) {
          newLocationAddress = reverseGeocodeName;
          console.log('✅ Reverse geocoding successful:', newLocationAddress);
        } else {
          console.warn('⚠️ Reverse geocoding failed, using coordinates as fallback');
          newLocationAddress = `${newPosition.latitude.toFixed(6)}, ${newPosition.longitude.toFixed(6)}`;
        }
      } catch (geocodingError) {
        console.warn('⚠️ Reverse geocoding error:', geocodingError);
        newLocationAddress = `${newPosition.latitude.toFixed(6)}, ${newPosition.longitude.toFixed(6)}`;
      }

      // Update incident in Firestore
      const incidentRef = doc(db, 'c3_incidents', incidentId);
      const updateData = {
        latitude: newPosition.latitude,
        longitude: newPosition.longitude,
        manuallyPositioned: true,
        repositionedAt: Timestamp.now(),
        repositionedBy: currentUser.uid,
        repositionedByName: currentUser.displayName || currentUser.email,
        lastUpdated: Timestamp.now()
      };

      // 🎯 UPDATE LOCATION FIELD: Add the new address to the location field
      if (newLocationAddress) {
        updateData.location = newLocationAddress;
        updateData.formattedLocation = newLocationAddress;
        updateData.geocodedAt = Timestamp.now();
        updateData.geocodingSource = 'reverse_geocode_drag';
        
        if (reverseGeocodeName) {
          updateData.reverseGeocodeName = reverseGeocodeName;
        }
      }

      await updateDoc(incidentRef, updateData);

      console.log('✅ PHASE 4: Crisis location and address updated successfully');
      message.success(`Crisis location updated: ${newLocationAddress || 'Coordinates updated'}`);
      
    } catch (error) {
      console.error('❌ PHASE 4: Error updating crisis location:', error);
      message.error('Failed to update crisis location');
    }
  };

  // Extract crisis location from incident data with granular dependencies
  const crisisLocation = useMemo(() => {

    if (!incident) return null;

    // Try to extract location information from various fields
    let location = null;

    // Check if incident has explicit location coordinates
    if (incident.latitude && incident.longitude && 
        !isNaN(incident.latitude) && !isNaN(incident.longitude)) {
      location = {
        latitude: parseFloat(incident.latitude),
        longitude: parseFloat(incident.longitude),
        title: incident.title || 'Crisis Location',
        address: incident.location || 'Unknown Address',
        formattedAddress: incident.formattedLocation || null,
        reverseGeocodeName: incident.reverseGeocodeName || null, // NEW: Human-readable location name
        geocodedAt: incident.geocodedAt || null,
        geocodingSource: incident.geocodingSource || null,
        // 🆕 PHASE 4: Manual positioning metadata
        manuallyPositioned: incident.manuallyPositioned || false,
        repositionedAt: incident.repositionedAt || null,
        repositionedBy: incident.repositionedBy || null,
        repositionedByName: incident.repositionedByName || null
      };
    } 
    // Check if there's location text that might be geocoded
    else if (incident.location && typeof incident.location === 'string' && incident.location.trim().length > 0) {
      location = {
        title: incident.title || 'Crisis Location',
        address: incident.location,
        needsGeocoding: true
      };
    }

    return location;
  }, [
    incident?.latitude, 
    incident?.longitude, 
    incident?.location, 
    incident?.title,
    incident?.formattedLocation,
    incident?.reverseGeocodeName,
    incident?.geocodedAt,
    incident?.geocodingSource,
    // 🆕 PHASE 4: Manual positioning dependencies
    incident?.manuallyPositioned,
    incident?.repositionedAt,
    incident?.repositionedBy,
    incident?.repositionedByName
  ]);

  // Get initial map center and zoom based on crisis location or default to organization region
  const getMapCenter = () => {
    if (crisisLocation && crisisLocation.latitude && crisisLocation.longitude) {
      return {
        lat: crisisLocation.latitude,
        lng: crisisLocation.longitude
      };
    }
    
    // Default to Australia center - this could be enhanced to use org location
    return { lat: -25.2744, lng: 133.7751 };
  };

  const getMapZoom = () => {
    // If we have a crisis location, zoom in closer
    if (crisisLocation && crisisLocation.latitude && crisisLocation.longitude) {
      return 15; // Even closer zoom for crisis epicenter view
    }
    return 5; // Default zoom level
  };

  // 🔬 SURGICAL FIX: Use props instead of local state
  // handleSitesSelected and handleRefresh are now passed as props

  // Check if user has access
  if (!currentUser?.orgId) {
    return (
      <Alert
        message="Access Denied"
        description="You need to be part of an organisation to access the mapping system."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="crisis-map-tab" style={{ padding: '16px' }}>
      {/* Crisis Info Header */}
      <div className="mb-4">
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size="small">
              <div className="d-flex align-items-center gap-2">
                <EnvironmentOutlined style={{ color: '#39325b', fontSize: '18px' }} />
                <Title level={4} style={{ margin: 0, color: '#39325b' }}>
                  Crisis Location and Site Management
                </Title>
              </div>
              
              {incident && (
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Text strong>{incident.title}</Text>
                  <Tag color="orange">{incident.status || 'Active'}</Tag>
                  
                  {crisisLocation && (
                    <>
                      <Text type="secondary">•</Text>
                      {crisisLocation.needsGeocoding ? (
                        <Text type="secondary">
                          <InfoCircleOutlined className="me-1" />
                          Location: {crisisLocation.address} (address needs geocoding)
                        </Text>
                      ) : (
                        <Text type="secondary">
                          <EnvironmentOutlined className="me-1" />
                          {crisisLocation.address}
                        </Text>
                      )}
                    </>
                  )}
                </div>
              )}
            </Space>
          </Col>
          
          <Col>
            <Button 
              icon={<ReloadOutlined />}
                              onClick={handleMapRefresh}
              type="default"
              size="small"
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </div>

      {/* Location Status Display */}
      {crisisLocation?.needsGeocoding && (
        <Alert
          message="Crisis Location Being Processed"
          description={`The crisis location "${crisisLocation.address}" is being automatically geocoded for map display. This happens automatically when you save the incident.`}
          type="info"
          showIcon
          className="mb-3"
          action={
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Auto-geocoding enabled
            </Text>
          }
        />
      )}
      


      {/* Main Content - NOW WITH FULL FEATURE PARITY */}
      <Row gutter={[16, 0]}>
        {/* Left Sidebar - Drawing and Measurement Tools (NEW - PHASE 1) */}
        <Col xs={24} lg={6}>
          <div style={{ height: '690px', overflowY: 'auto' }}>
            <AdvancedDrawingTools
              map={mapInstance}
              onShapeComplete={(bounds, mode) => {
                // Handle shape completion and site selection
            
              }}
              onSelectionChange={(bounds, mode) => {
                // Handle selection changes during editing
            
              }}
              // Phase 3: Measurement tool integration (synced with MapCore)
              measurementMode={measurementMode}
              onStartMeasurement={mapInstance?.measurementFunctionsRef?.current?.startMeasurement}
              onClearMeasurement={mapInstance?.measurementFunctionsRef?.current?.clearMeasurement}
              // Phase 3: Pass measurement functions ref for circle integration
              measurementFunctionsRef={mapInstance?.measurementFunctionsRef}
              // Phase 2: Pass unit preference props (universal)
              unitPreference={unitPreference}
              onUnitPreferenceChange={handleUnitPreferenceChange}
              // Phase 2: Pass ring styling props (universal)
              ringStyleConfig={ringStyleConfig}
              onRingStyleChange={handleRingStyleChange}
              // 🎯 CRITICAL FIX: Circle site detection connection - pass the correct callback
              onCircleComplete={handleCircleComplete}
            />
            
            {/* Spacer */}
            <div style={{ marginTop: '16px' }}>
              <HazardOverlayLayer
                map={mapInstance}
                showControls={true}
                onHazardClick={(hazard) => {
              
                }}
                // Site icons toggle control
                showSiteIcons={showSiteIcons}
                onShowSiteIconsChange={setShowSiteIcons}
                // Site color mode toggle control
                siteColorMode={siteColorMode}
                onSiteColorModeChange={setSiteColorMode}
              />
            </div>
          </div>
        </Col>

        {/* Map Column - 🔬 SURGICAL FIX: MapCore state managed at IncidentDetailsTabs level */}
        <Col xs={24} lg={selectedSites.length > 0 ? 12 : 18} style={{ paddingBottom: 0, marginBottom: 0 }}>
          <div className="dashboard-map-container" style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            marginBottom: 0 
          }}> {/* 🎯 CRISIS FIX: Enable absolute positioning for popup + flex layout for legend overlay */}
            <style>
              {`
                .crisis-map .map-container {
                  margin-bottom: 0 !important;
                }
              `}
            </style>
            <MapCore
              mapId={`crisis-map-${incidentId}`}
              height="580px"
              showDrawingTools={true}
              showSiteSelection={true}
              initialCenter={getMapCenter()}
              initialZoom={getMapZoom()}
              crisisLocation={crisisLocation?.latitude && crisisLocation?.longitude ? crisisLocation : null}
              onSitesSelected={handleSitesSelected}
              onMapLoad={handleMapLoad}
              onSitesLoaded={handleSitesLoaded}
              onMeasurementModeChange={handleMeasurementModeChange}
              unitPreference={unitPreference}
              ringStyleConfig={ringStyleConfig}
              useAdvancedDrawingTools={true}
              showSiteIcons={showSiteIcons}
              siteColorMode={siteColorMode}
              onCrisisLocationDrag={handleCrisisLocationDrag}
              onCircleComplete={handleCircleComplete}
              onSitePopupStateChange={handleSitePopupStateChange}
              onSitePopupDataChange={handleSitePopupDataChange}
              className="crisis-map"
            />
          </div>
          
          {/* 🎯 CRITICAL FIX: DISABLED CrisisMapTab popup to prevent dual popups - MapCore handles popup now */}
          {false && showSitePopup && sitePopupData && (
            <div style={{ 
              position: 'absolute',
              top: '20px', // 20px from top of crisis map container (simpler layout)
              right: '20px', 
              zIndex: 1000,
              maxWidth: '400px',
              maxHeight: 'calc(100% - 60px)', // Relative to crisis map container height
              overflow: 'auto'
            }}>
              <SiteListPopup
                sites={sitePopupData.sites}
                circle={sitePopupData.circle}
                onClose={sitePopupData.onClose}
              />
            </div>
          )}
          
          {/* Site Status Legend - Below Map */}
          {showSiteIcons && (
            <div style={{ flexShrink: 0, paddingTop: '2px', marginTop: 0 }}>
              <MapLegend 
                siteColorMode={siteColorMode}
                sites={allSites}  // NEW: Pass sites data
                siteCounts={siteCounts}  // NEW: Pass site counts
                context="crisis"
                key={`legend-${allSites.length}`}  // NEW: Force re-render when sites change
              />
            </div>
          )}
        </Col>

        {/* Site Selection Panel */}
        {selectedSites.length > 0 && (
          <Col xs={24} lg={6}>
            <div style={{ height: '580px', overflowY: 'auto' }}>
              <SiteSelectionPanel
                selectedSites={selectedSites}
                showMassContact={true}
                title="Selected Sites for Crisis Communication"
                emptyMessage="Draw a shape on the map to select sites affected by this crisis."
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CrisisMapTab; 